import React from 'react'
import { css, StyleSheet } from 'aphrodite'
import Tooltip from '@material-ui/core/Tooltip'
import CircularProgress from '@material-ui/core/CircularProgress'

import { BaseEntityPage } from './BaseEntityPage'
import { Helper } from '../helpers/Helper'
import { ApiHelper } from '../helpers/ApiHelper'
import { UiHelper } from '../helpers/UiHelper'
import { AppConfig } from '../../AppConfig'
import { AppStyles, AppTheme } from '../../styles/AppTheme'
import { SearchBar } from './SearchBar'
import { PageDialog } from './PageDialog'

const BRIDGE_PATH = 'whatsappvendorprojects'
const WA_POLL_INTERVAL_MS = 5000

function makeHooksUrl(ms, orgId, id) {
  const paths = id ? ['hooks', id] : ['hooks']
  return ApiHelper.makeUrlPath2({
    ms,
    paths,
    queryParams: { orgId, bridgePath: BRIDGE_PATH },
  })
}

function normalizeVendorProjectsResponse(data) {
  if (!data) return []
  if (Array.isArray(data)) return data
  if (Array.isArray(data.items)) return data.items
  return []
}

async function getVendorProjectById(ms, orgId, vendorProjectId) {
  const response = await ApiHelper.callAwait({
    ms,
    method: 'GET',
    paths: ['hooks'],
    queryParams: { orgId, bridgePath: BRIDGE_PATH },
  })
  const items = normalizeVendorProjectsResponse(response)
  return items.find(item => item.id === vendorProjectId) || null
}

function filterVendorProjectsRows(masterList, searchText) {
  const q = String(searchText || '').trim().toLowerCase()
  if (!q) return masterList || []
  return (masterList || []).filter(row =>
    String(row.name || '').toLowerCase().includes(q) ||
    String(row.number || '').toLowerCase().includes(q)
  )
}

function buildAddFieldDef(orgId, orgTitle) {
  return {
    title: 'Add Vendor Project',
    steps: [{
      fields: [{
        key: 'orgId',
        type: 'radio',
        label: 'Organisation',
        options: [{ key: orgId, label: orgTitle || orgId }],
        defaultValue: orgId,
        v_required: { value: false, err: 'Organisation is required' }, // eslint-disable-line camelcase
      }],
    }],
    disableAlwaysFormFields: ['orgId'],
  }
}

function buildPhoneNumberFieldDef() {
  return {
    title: 'Enter Phone Number',
    isWaNumberEntry: true,
    steps: [{
      fields: [{
        key: 'number',
        type: 'edit_text',
        label: 'Phone Number',
        v_required: { value: true, err: 'Phone number is required' }, // eslint-disable-line camelcase
      }],
    }],
  }
}

function getApiErrorMessage(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null
  if (!('respCode' in data) && !('message' in data)) return null
  if (data.respCode === 0) return null
  return data.message ? String(data.message).trim() : Helper.getString('customerCareErr')
}

function getVendorProjectNumber(vendorProject) {
  return String(vendorProject && vendorProject.number || '').trim()
}

async function callWhatsappOnboardNumber(ms, orgId, vendorProjectId, number) {
  return ApiHelper.callAwait({
    ms,
    method: 'POST',
    paths: ['miscs', 'whatsapp-onboard-number'],
    queryParams: { orgId },
    jsonBody: { vendorProjectId, number },
  })
}

function hasWhatsAppProvider(providers) {
  return (providers || []).some(p => String(p).toUpperCase() === 'WHATSAPP')
}

function statusBadgeStyle(status) {
  const s = String(status || '').toUpperCase()
  if (['APPROVED', 'ACTIVE', 'LIVE'].includes(s)) return Styles.statusBadgeSuccess
  if (['PENDING', 'IN_REVIEW'].includes(s)) return Styles.statusBadgePending
  if (['REJECTED', 'FAILED', 'DISABLED'].includes(s)) return Styles.statusBadgeError
  return Styles.statusBadgeDefault
}

function renderStatusBadge(status) {
  const label = String(status || '').trim()
  if (!label) return '—'
  return (
    <span className={css(Styles.statusBadge, statusBadgeStyle(label))}>
      {label}
    </span>
  )
}

function WaPollingDialogContent() {
  return (
    <div className={css(Styles.waPollingContent)}>
      <CircularProgress />
      <span>Waiting for connection ...</span>
    </div>
  )
}

export class WAVendorProjects extends BaseEntityPage {
  constructor(props, options) {
    super(props)
    this.ms = AppConfig.CDP_MS.ENDPOINT
    this.pageTitle = Helper.getString('wa-vendor-projects')
    this.pageKey = 'wa-vendor-projects'
    this.orgId = this.props.match.params.orgId || 'PRODUCT'
    this.emptyText = Helper.getString('emptyMsg')
    this.pollActive = false
    this.waPollTimerId = null
    this.hasInitiatedWaPost = false
    this.breadCrumbs = [
      { title: Helper.getString('home'), to: this.baseRoute() },
      { title: this.pageTitle },
    ]
    this.toFetch = [
      {
        key: 'items',
        ms: this.ms,
        method: 'GET',
        paths: ['hooks'],
        queryParams: { orgId: this.orgId, bridgePath: BRIDGE_PATH },
      },
      {
        key: 'crmnumbers',
        ms: this.ms,
        method: 'GET',
        paths: ['crmnumbers'],
        queryParams: { orgId: this.orgId },
      },
    ]
    this.apiData = { orgId: this.orgId }
    this.cellWidths = {
      Index: Styles.cellWidthSmall,
      Name: Styles.cellWidthMedium,
      Number: Styles.cellWidthMedium,
      Status: Styles.cellWidthNormal,
      actions: Styles.cellWidthNormal,
    }
    this.tableHeadersMap = {
      Index: current => current.__index + 1,
      Name: current => current.name || '—',
      Number: current => current.number || '—',
      Status: current => renderStatusBadge(current.status),
    }
    this.tableHeaders = Object.keys(this.tableHeadersMap)
    this.headerActions = [
      {
        label: 'Add new',
        onClick: () => this.openModalForAdd(),
      },
      {
        label: 'Refresh',
        tooltip: 'Refresh',
        icon: 'refresh',
        type: 'icon-label',
        onClick: () => {
          this.setState({ loadingMode: ApiHelper.LOADING_MODE.PAGE_LOAD }, () => this.fetchItems())
        },
      },
    ]
    this.state = {
      ...this.state,
      masterVendorProjectList: [],
      searchText: '',
      crmVendorProjectIds: [],
      showWaPollingDialog: false,
      waTabOpened: false,
      pollActive: false,
    }
  }

  componentWillUnmount() {
    this.abortWaPolling()
  }

  onFetchSuccess(results) {
    const raw = this.getResult(results, 'items')
    const masterVendorProjectList = normalizeVendorProjectsResponse(raw).slice().reverse()
    const items = filterVendorProjectsRows(masterVendorProjectList, this.state.searchText)
    const crmnumbers = this.getResult(results, 'crmnumbers')
    const crmVendorProjectIds = ((crmnumbers && crmnumbers.items) || [])
      .map(item => item.waVendorProjectId)
      .filter(Boolean)
    this.setState({ masterVendorProjectList, items, crmVendorProjectIds, crmnumbers })
  }

  renderAboveTable() {
    return (
      <div
        className={css(Styles.searchBarWrapper)}
        onKeyDownCapture={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
          }
        }}
      >
        <SearchBar
          hint='Search by name or number'
          onSearch={this.onSearch.bind(this)}
          onClearSearch={this.clearSearch.bind(this)}
          searchText={this.state.searchText}
          searchButtonLabel='Search'
          clearSearchLabel='Clear Search'
          autoRefresh
          debounceDuration={1}
          autoFocus
        />
      </div>
    )
  }

  renderBelowTable() {
    return (
      <PageDialog
        isOpen={this.state.showWaPollingDialog}
        title='Connect to WhatsApp'
        onClosed={() => this.closeWaPollingDialog()}
        fullWidth
        maxWidth='sm'
        PageComponent={WaPollingDialogContent}
      />
    )
  }

  onSearch(searchText = '') {
    const items = filterVendorProjectsRows(this.state.masterVendorProjectList, searchText)
    this.setState({ searchText, items })
  }

  clearSearch() {
    this.setState({
      searchText: '',
      items: this.state.masterVendorProjectList || [],
    })
  }

  onTableValue(current, index) {
    if (!this.tableHeaders[index]) return ''
    return this.tableHeadersMap[this.tableHeaders[index]](current)
  }

  openModalForAdd() {
    this.waVendorProjectForConnect = null
    const orgTitle = UiHelper.getOrgTitle() || this.orgId
    super.openModalForAdd(buildAddFieldDef(this.orgId, orgTitle))
  }

  openModalForWaNumber(current) {
    this.waVendorProjectForConnect = current
    this.formDefinition = buildPhoneNumberFieldDef()
    this.formDefinition.formType = 'add'
    Helper.deleteFormDefinitionValues(this.formDefinition)
    this.setState({ modalOpen: true })
  }

  customSubmit(options) {
    const { formData, caller } = options
    if (this.formDefinition && this.formDefinition.isWaNumberEntry) {
      const number = String(formData.number || '').trim()
      const vendorProject = this.waVendorProjectForConnect
      this.waVendorProjectForConnect = null
      this.setState({ modalOpen: false })
      if (number && vendorProject) {
        this.initiateWaPolling(vendorProject, number)
      }
      return
    }
    return caller.submit(formData, true)
  }

  onAdd() {
    this.setState({
      apiMethod: 'POST',
      apiEndPoint: makeHooksUrl(this.ms, this.orgId),
      apiData: { orgId: this.orgId },
    })
  }

  startWaOnboarding(current) {
    const number = getVendorProjectNumber(current)
    if (number) {
      this.initiateWaPolling(current, number)
    } else {
      this.openModalForWaNumber(current)
    }
  }

  async syncLiveVendorProject(current) {
    const number = getVendorProjectNumber(current)
    if (!number) {
      this.setState({
        showToast: true,
        isErrorToast: true,
        toastMsg: 'Phone number is required',
      })
      return
    }
    try {
      const response = await callWhatsappOnboardNumber(this.ms, this.orgId, current.id, number)
      const apiError = getApiErrorMessage(response)
      if (apiError) {
        this.setState({ showToast: true, isErrorToast: true, toastMsg: apiError })
        return
      }
      this.fetchItems()
    } catch (e) {
      this.setState({
        showToast: true,
        isErrorToast: true,
        toastMsg: Helper.getErrorMsg(e),
      })
    }
  }

  initiateWaPolling(vendorProject, number) {
    if (this.pollActive) return
    this.hasInitiatedWaPost = false
    this.pollActive = true
    this.setState({
      showWaPollingDialog: true,
      waTabOpened: false,
      pollActive: true,
    }, () => this.pollWhatsappOnboarding(vendorProject.id, number))
  }

  abortWaPolling() {
    this.pollActive = false
    this.hasInitiatedWaPost = false
    if (this.waPollTimerId) {
      clearTimeout(this.waPollTimerId)
      this.waPollTimerId = null
    }
  }

  closeWaPollingDialog() {
    this.abortWaPolling()
    this.setState({
      showWaPollingDialog: false,
      pollActive: false,
    })
  }

  stopWaPolling({ isError, message, refresh }) {
    this.abortWaPolling()
    this.setState({
      showWaPollingDialog: false,
      pollActive: false,
      showToast: !!(message && isError),
      isErrorToast: !!isError,
      toastMsg: message,
    })
    if (refresh) this.fetchItems()
  }

  async pollWhatsappOnboarding(vendorProjectId, number) {
    if (!this.pollActive) return

    try {
      if (!this.hasInitiatedWaPost) {
        const response = await callWhatsappOnboardNumber(this.ms, this.orgId, vendorProjectId, number)
        const apiError = getApiErrorMessage(response)
        if (apiError) {
          this.stopWaPolling({ isError: true, message: apiError })
          return
        }
        const signupUrl = response && response.signupResponse && response.signupResponse.embeddedSignupURL
        if (signupUrl && !this.state.waTabOpened) {
          window.open(String(signupUrl).trim(), '_blank', 'noopener,noreferrer')
          this.setState({ waTabOpened: true })
        }
        const providers = response && response.crmNumber && response.crmNumber.providers
        if (hasWhatsAppProvider(providers)) {
          this.stopWaPolling({ refresh: true })
          return
        }
        this.hasInitiatedWaPost = true
      } else {
        const item = await getVendorProjectById(this.ms, this.orgId, vendorProjectId)
        if (item && String(item.status).toUpperCase() === 'LIVE') {
          this.hasInitiatedWaPost = false
        }
      }
      if (this.pollActive) {
        this.waPollTimerId = setTimeout(
          () => this.pollWhatsappOnboarding(vendorProjectId, number),
          WA_POLL_INTERVAL_MS
        )
      }
    } catch (e) {
      this.stopWaPolling({ isError: true, message: Helper.getErrorMsg(e) })
    }
  }

  onActions = (current) => {
    const actions = []
    const isPending = String(current.status).toUpperCase() === 'PENDING'
    const isLive = String(current.status).toUpperCase() === 'LIVE'
    const mappedCrmNumber = this.state.crmnumbers && this.state.crmnumbers.items && this.state.crmnumbers.items.find(item => item.waVendorProjectId === current.id)
    if (isPending || (isLive && (!mappedCrmNumber || !(mappedCrmNumber.providers || []).includes('WHATSAPP')))) {
      actions.push(
        <Tooltip title='Connect to WhatsApp' placement='top-start' key={`${current.id}-wa`}>
          <span
            className={css(AppStyles.entityTableLinkIcon, Styles.rowActionIconWrapper)}
            onClick={() => (isLive ? this.syncLiveVendorProject(current) : this.startWaOnboarding(current))}
          >
            <i className='material-icons-outlined'>add_link</i>
          </span>
        </Tooltip>
      )
    }
    return actions
  }
}

const Styles = StyleSheet.create({
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    minWidth: 88,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: '16px',
    padding: '4px 10px',
    textTransform: 'uppercase',
    letterSpacing: 0.2,
  },
  statusBadgeDefault: {
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
  },
  statusBadgeSuccess: {
    backgroundColor: '#3BDE86',
    color: '#222222',
  },
  statusBadgePending: {
    backgroundColor: '#FFD7BB',
    color: '#222222',
  },
  statusBadgeError: {
    backgroundColor: '#FEE2E2',
    color: '#991B1B',
  },
  cellWidthSmall: {
    width: '10%',
  },
  cellWidthNormal: {
    width: '20%',
  },
  cellWidthMedium: {
    width: '30%',
  },
  cellWidthWide: {
    width: '50%',
  },
  searchBarWrapper: {
    width: '100%',
    maxWidth: AppTheme.viewPort,
  },
  waPollingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 16,
    padding: 24,
    minWidth: 240,
  },
  rowActionIconWrapper: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 0,
    height: 28,
    width: 28,
  },
  waConnectIcon: {
    fontSize: 24,
    width: 24,
    height: 24,
    display: 'block',
  },
})
